import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { ExternalLink, Eye, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export default function Stores() {
  const [stores, setStores] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getStores({ page, limit: 20 });
      setStores(data.data);
      setTotal(data.total);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStores(); }, [page]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this store?")) return;
    try {
      await adminApi.deleteStore(id);
      toast.success("Store deleted");
      fetchStores();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Get first product image from collections
  const getPreviewImage = (store) => {
    const collections = store.collections || [];
    for (const col of collections) {
      const products = col.products || [];
      for (const product of products) {
        const imgUrl = product.image || product.image_url;
        if (imgUrl) {
          if (imgUrl.startsWith("/api")) {
            return `${API}${imgUrl}`;
          }
          return imgUrl;
        }
      }
    }
    return null;
  };

  // Get logo URL
  const getLogoUrl = (store) => {
    return store.brand?.logo_url || store.brand?.profile_image || null;
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Stores ({total})</h1>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Preview</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Colors</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.map((store) => {
                const previewImg = getPreviewImage(store);
                const logoUrl = getLogoUrl(store);
                const colors = store.brand?.colors || [];
                const productCount = (store.collections || []).reduce(
                  (acc, col) => acc + (col.products?.length || 0), 0
                );

                return (
                  <TableRow key={store.id}>
                    <TableCell>
                      <div className="flex gap-1">
                        {logoUrl && (
                          <img
                            src={logoUrl}
                            alt="Logo"
                            className="w-10 h-10 object-contain rounded border bg-white"
                          />
                        )}
                        {previewImg && (
                          <img
                            src={previewImg}
                            alt="Preview"
                            className="w-10 h-10 object-cover rounded border"
                          />
                        )}
                        {!logoUrl && !previewImg && (
                          <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-gray-400 text-xs">
                            -
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{store.brand?.name || "-"}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[150px]">
                        {store.source_url}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-xs text-gray-500">
                      {store.source_url}
                    </TableCell>
                    <TableCell>
                      <Badge variant={store.source_type === "instagram" ? "default" : "secondary"}>
                        {store.source_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        {colors.slice(0, 4).map((color, i) => (
                          <div
                            key={i}
                            className="w-4 h-4 rounded-full border border-gray-200"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{productCount}</Badge>
                    </TableCell>
                    <TableCell>{store.views}</TableCell>
                    <TableCell className="text-xs">
                      {new Date(store.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <a
                          href={`/store/${store.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View public store"
                        >
                          <Button variant="outline" size="sm">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                        <Link to={`/admin/stores/${store.id}`} title="View details">
                          <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(store.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="flex justify-center gap-2 mt-4">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className="py-2 px-4">Page {page}</span>
            <Button variant="outline" disabled={stores.length < 20} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
